import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, roleLabel } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, BookOpen, X } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/errors";
import { HELP_SECTIONS, topicText, snippet } from "@/lib/help";
import HelpTopicView from "@/components/help/HelpTopicView";
import HelpTopicEditor from "@/components/help/HelpTopicEditor";

function Sidebar({ grouped, activeId, onPick }) {
  return (
    <nav className="space-y-6 lg:sticky lg:top-6" data-testid="help-sidebar">
      {grouped.map(([section, items]) => (
        <div key={section}>
          <p className="font-overline text-[10px] text-slate-500 mb-2">{section}</p>
          <ul className="border-l-2 border-slate-200">
            {items.map(t => (
              <li key={t.id}>
                <button onClick={() => onPick(t)} data-testid={`help-nav-${t.slug}`}
                  className={`block w-full text-left text-sm pl-3 py-1.5 -ml-[2px] border-l-2 transition-colors duration-150 ${t.id === activeId ? "border-[#E11D48] text-[#0A1128] font-semibold" : "border-transparent text-slate-600 hover:text-[#0A1128] hover:border-slate-400"}`}>
                  {t.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function MobileTopicPicker({ grouped, active, onPick, topics }) {
  return (
    <div className="lg:hidden" data-testid="help-mobile-picker">
      <Select value={active?.id || ""} onValueChange={id => onPick(topics.find(t => t.id === id))}>
        <SelectTrigger className="rounded-sm h-11 bg-white" data-testid="help-mobile-select"><SelectValue placeholder="Jump to a topic"/></SelectTrigger>
        <SelectContent>
          {grouped.map(([section, items]) => (
            <SelectGroup key={section}>
              <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{section}</SelectLabel>
              {items.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const Marked = ({ text, term }) => {
  const i = text.toLowerCase().indexOf(term.toLowerCase());
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="bg-[#FACC15] px-0.5">{text.slice(i, i + term.length)}</mark>{text.slice(i + term.length)}</>;
};

function SearchResults({ results, term, onPick }) {
  return (
    <div className="bg-white border border-slate-300" data-testid="help-search-results">
      <p className="px-5 py-3 border-b text-sm text-slate-600">{results.length} result{results.length === 1 ? "" : "s"} for "<strong>{term}</strong>"</p>
      {results.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="help-search-empty">No guide topics match. Try another keyword.</p>}
      <ul className="divide-y">
        {results.map(t => (
          <li key={t.id}>
            <button onClick={() => onPick(t)} className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors" data-testid={`help-result-${t.slug}`}>
              <p className="font-overline text-[10px] text-[#E11D48]">{t.section}</p>
              <p className="font-semibold text-[#0A1128]"><Marked text={t.title} term={term}/></p>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2"><Marked text={snippet(t, term)} term={term}/></p>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HelpPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [topics, setTopics] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const isSuper = user?.role === "super_admin";
  const term = q.trim();

  const load = () => api.get("/help/topics").then(r => setTopics(r.data)).catch(() => setTopics([]));
  useEffect(() => { if (user !== undefined) load(); }, [user]);

  const grouped = useMemo(() => {
    const secs = [...HELP_SECTIONS, ...new Set(topics.map(t => t.section).filter(s => !HELP_SECTIONS.includes(s)))];
    return secs.map(s => [s, topics.filter(t => t.section === s)]).filter(([, items]) => items.length);
  }, [topics]);
  const results = useMemo(() => (term ? topics.filter(t => topicText(t).includes(term.toLowerCase())) : []), [topics, term]);
  const active = topics.find(t => t.slug === params.get("topic")) || topics[0];

  const pick = (t) => { setParams({ topic: t.slug }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const openFromSearch = (t) => { pick(t); };
  const del = async () => {
    if (!window.confirm(`Delete "${active.title}"?`)) return;
    try { await api.delete(`/help/topics/${active.id}`); toast.success("Topic deleted"); setParams({}); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="help-page">
      <header className="mb-8 border-b-2 border-[#0A1128] pb-6">
        <p className="font-overline text-xs text-[#E11D48] mb-1 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5"/>Help &amp; User Guide{user && ` · ${roleLabel(user.role)}`}</p>
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mr-auto">HOW CAN WE HELP?</h1>
          {isSuper && (
            <Button onClick={() => setEditing("new")} className="rounded-sm bg-[#0A1128] hover:bg-slate-800 font-heading" data-testid="help-new-topic">
              <Plus className="w-4 h-4 mr-1"/>NEW TOPIC
            </Button>
          )}
        </div>
        <div className="relative mt-5 max-w-2xl">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the guide — e.g. import, password, audit…"
            className="rounded-sm pl-9 pr-9 h-11 border-slate-300" data-testid="help-search"/>
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="help-search-clear"><X className="w-4 h-4"/></button>}
        </div>
      </header>

      {topics.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="help-empty">No guide topics available yet.</p>
      ) : (
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="hidden lg:block"><Sidebar grouped={grouped} activeId={term ? null : active?.id} onPick={t => { setQ(""); pick(t); }}/></aside>
          <section className="min-w-0 space-y-6">
            <MobileTopicPicker grouped={grouped} active={active} topics={topics} onPick={t => { setQ(""); pick(t); }}/>
            {term && <SearchResults results={results} term={term} onPick={openFromSearch}/>}
            {active && (!term || results.some(r => r.id === active.id)) && (
              <HelpTopicView topic={active} term={term} canEdit={isSuper} onEdit={() => setEditing(active)} onDelete={del}/>
            )}
          </section>
        </div>
      )}

      {editing && (
        <HelpTopicEditor topic={editing === "new" ? null : editing} onClose={() => setEditing(null)}
          onSaved={(t) => { setEditing(null); load().then(() => setParams({ topic: t.slug })); }}/>
      )}
    </main>
  );
}
